import { useEffect, useState } from "react";
import { AlertTriangle } from "lucide-react";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type {
  ReplenishmentMode,
  SetReplenishmentPolicyRequest,
  StockLocation,
  StockReplenishmentPanel,
} from "@shared-core/data/api-types";
import { ApiError } from "@shared-core/data/api/errors";
import { formatCount } from "@shared-core/domain/formatters";
import { Button } from "../../app/components/primitives/button";
import { Checkbox } from "../../app/components/primitives/checkbox";
import { Input } from "../../app/components/primitives/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../app/components/primitives/dialog";
import { Label } from "../../app/components/primitives/label";
import {
  FieldShell,
  type FieldErrorMap,
  selectClassName,
  toFieldErrorMap,
} from "./StockFormField";

interface ReplenishmentPolicyDialogProps {
  panel: StockReplenishmentPanel;
  onClose: () => void;
  onSaved: () => void;
}

interface CsrOption {
  id: string;
  name: string;
}

/** Create or change the item's replenishment policy. Only changed fields are
 * sent; choosing a mode records it as set by a person. */
export function ReplenishmentPolicyDialog({
  panel,
  onClose,
  onSaved,
}: ReplenishmentPolicyDialogProps) {
  const dataAdapter = useDataAdapter();
  const { policy } = panel;
  const [mode, setMode] = useState<ReplenishmentMode | "">(policy?.mode ?? "");
  const [warehouseId, setWarehouseId] = useState(
    policy?.delivery_warehouse_id ?? "",
  );
  const [csrId, setCsrId] = useState(policy?.stock_line_owner_user_id ?? "");
  const [routeCode, setRouteCode] = useState(
    policy?.preferred_route_code ?? "",
  );
  const [neededWithin, setNeededWithin] = useState(
    policy?.needed_within_days == null ? "" : String(policy.needed_within_days),
  );
  const [isTharsternMake, setIsTharsternMake] = useState(
    policy?.is_tharstern_make ?? false,
  );
  const [locations, setLocations] = useState<StockLocation[]>([]);
  const [csrs, setCsrs] = useState<CsrOption[]>([]);
  const [fieldErrors, setFieldErrors] = useState<FieldErrorMap>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    dataAdapter
      .listStockLocations()
      .then((result) => !cancelled && setLocations(result))
      .catch((error: unknown) => {
        console.error("stock.locations.load_failed", {
          status: error instanceof ApiError ? error.status : undefined,
        });
      });
    dataAdapter
      .listUsers({ roles: ["csr"], pageSize: 100 })
      .then(
        (result) =>
          !cancelled &&
          setCsrs(result.items.map((u) => ({ id: u.id, name: u.name }))),
      )
      .catch((error: unknown) => {
        console.error("stock.replenishment.csrs_load_failed", {
          status: error instanceof ApiError ? error.status : undefined,
        });
      });
    return () => {
      cancelled = true;
    };
  }, [dataAdapter]);

  // The current stock-line owner stays choosable even when the list doesn't carry them.
  const csrOptions =
    policy?.stock_line_owner_user_id &&
    !csrs.some((c) => c.id === policy.stock_line_owner_user_id)
      ? [
          {
            id: policy.stock_line_owner_user_id,
            name:
              panel.stock_line_owner_name ?? policy.stock_line_owner_user_id,
          },
          ...csrs,
        ]
      : csrs;

  function changes(): SetReplenishmentPolicyRequest {
    const body: SetReplenishmentPolicyRequest = {};
    if (mode && mode !== policy?.mode) body.mode = mode;
    if (warehouseId && warehouseId !== policy?.delivery_warehouse_id)
      body.delivery_warehouse_id = warehouseId;
    if (csrId !== (policy?.stock_line_owner_user_id ?? ""))
      body.stock_line_owner_user_id = csrId || null;
    if (routeCode !== (policy?.preferred_route_code ?? ""))
      body.preferred_route_code = routeCode || null;
    const days = neededWithin.trim() === "" ? null : Number(neededWithin);
    if (days !== (policy?.needed_within_days ?? null))
      body.needed_within_days = days;
    if (isTharsternMake !== (policy?.is_tharstern_make ?? false))
      body.is_tharstern_make = isTharsternMake;
    return body;
  }

  const body = changes();
  const hasChanges = policy == null || Object.keys(body).length > 0;

  async function handleSave() {
    setIsSaving(true);
    setFieldErrors({});
    setFormError(null);
    try {
      await dataAdapter.setReplenishmentPolicy(panel.stock_item_id, body);
      onSaved();
    } catch (error) {
      console.error("stock.replenishment.policy_save_failed", {
        status: error instanceof ApiError ? error.status : undefined,
      });
      const map = toFieldErrorMap(error);
      setFieldErrors(map);
      setFormError(
        Object.keys(map).length > 0
          ? "Please fix the highlighted fields and try again."
          : error instanceof ApiError && error.status === 409
            ? error.detail
            : "Couldn't save the policy. Please try again.",
      );
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {policy ? "Edit replenishment policy" : "Set up replenishment"}
          </DialogTitle>
          <DialogDescription>
            {panel.stock_code} · {panel.owner.name ?? "Owner name unavailable"}.
            Re-order at{" "}
            {panel.reorder_level == null
              ? "–"
              : formatCount(panel.reorder_level)}{" "}
            / quantity{" "}
            {panel.reorder_quantity == null
              ? "–"
              : formatCount(panel.reorder_quantity)}
            : change those with Edit details on the stock item.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {formError && (
            <div
              role="alert"
              className="flex items-start gap-2 rounded-lg border border-red-300 bg-red-50 dark:bg-red-900/20 dark:border-red-800 p-3 text-sm text-red-700 dark:text-red-300"
            >
              <AlertTriangle className="size-4 shrink-0 mt-0.5" />
              {formError}
            </div>
          )}

          <FieldShell
            id="policy-mode"
            label="Mode"
            error={fieldErrors.mode}
            hint="Automatic raises the reprint at once when spec and artwork are in place; advise waits for a person. Choosing one records it as set by a person rather than the owner's default."
          >
            {(aria) => (
              <select
                id="policy-mode"
                className={selectClassName}
                value={mode}
                onChange={(e) => setMode(e.target.value as ReplenishmentMode)}
                {...aria}
              >
                {!policy && <option value="">Owner's default</option>}
                <option value="advise">Advise</option>
                <option value="automatic">Automatic</option>
              </select>
            )}
          </FieldShell>

          <FieldShell
            id="policy-warehouse"
            label="Deliver into"
            error={fieldErrors.delivery_warehouse_id}
            hint="The warehouse a reprint is delivered into."
          >
            {(aria) => (
              <select
                id="policy-warehouse"
                className={selectClassName}
                value={warehouseId}
                onChange={(e) => setWarehouseId(e.target.value)}
                {...aria}
              >
                <option value="" disabled>
                  Choose a warehouse
                </option>
                {locations.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.name}
                  </option>
                ))}
              </select>
            )}
          </FieldShell>

          <FieldShell
            id="policy-route"
            label="Preferred route"
            error={fieldErrors.preferred_route_code}
            hint="Pins sourcing to one route; leave as any to let sourcing choose."
          >
            {(aria) => (
              <select
                id="policy-route"
                className={selectClassName}
                value={routeCode}
                onChange={(e) => setRouteCode(e.target.value)}
                {...aria}
              >
                <option value="">Any route</option>
                {panel.preferred_route_options.map((r) => (
                  <option key={r.code} value={r.code}>
                    {r.label}
                  </option>
                ))}
              </select>
            )}
          </FieldShell>

          <FieldShell
            id="policy-needed-within"
            label="Needed within (working days)"
            error={fieldErrors.needed_within_days}
            hint="How long a reprint may take, to the supplier's despatch. Sourcing passes over slower routes; leave blank for no limit."
          >
            {(aria) => (
              <Input
                id="policy-needed-within"
                type="number"
                min={1}
                max={60}
                inputMode="numeric"
                value={neededWithin}
                onChange={(e) => setNeededWithin(e.target.value)}
                {...aria}
              />
            )}
          </FieldShell>

          <FieldShell
            id="policy-csr"
            label="Stock-line owner"
            error={fieldErrors.stock_line_owner_user_id}
            hint="The CSR told when an advise proposal is waiting."
          >
            {(aria) => (
              <select
                id="policy-csr"
                className={selectClassName}
                value={csrId}
                onChange={(e) => setCsrId(e.target.value)}
                {...aria}
              >
                <option value="">Nobody</option>
                {csrOptions.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            )}
          </FieldShell>

          <div className="flex items-start gap-2">
            <Checkbox
              id="policy-tharstern-make"
              checked={isTharsternMake}
              onCheckedChange={(checked) =>
                setIsTharsternMake(checked === true)
              }
            />
            <div className="space-y-0.5">
              <Label htmlFor="policy-tharstern-make">Tharstern (make)</Label>
              <p className="text-xs text-muted-foreground">
                Tharstern still reprints the stock item, so One will raise
                nothing for the stock item. Stays on while the spec is needed.
              </p>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            onClick={() => void handleSave()}
            disabled={isSaving || !hasChanges || !warehouseId}
          >
            {isSaving ? "Saving…" : "Save policy"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
